import { SidebarContextProviderAddUIFlagMutation } from '#app/__generated__/relay/SidebarContextProviderAddUIFlagMutation.graphql.js';
import { SidebarContextProviderRemoveUIFlagMutation } from '#app/__generated__/relay/SidebarContextProviderRemoveUIFlagMutation.graphql.js';
import {
  regexCommunity,
  regexDiscover,
} from '#app/components/AppBar/utilities/getAuthenticatedRouteLabel.js';
import { useContraMutation } from '#app/hooks/useContraMutation.js';
import { useCurrentUser } from '#app/hooks/useCurrentUser.js';
import { useIsMobile } from '#app/hooks/useIsMobile.js';
import { usePageContext } from '#app/hooks/usePageContext.js';
import { useRouter } from '#app/hooks/useRouter.js';
import { SidebarContext, SidebarContextType } from './SidebarContext.js';
import { useMediaQuery } from '@contra/react-hooks/useMediaQuery';
import { useMount } from '@contra/react-hooks/useMount';
import { graphql } from '@contra/relay';
import { captureException } from '@contra/sentry/react';
import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { useTheme } from 'styled-components';

const regexDiscoverJobs = /^\/discover\/jobs$/;

const SIDEBAR_OPEN_WIDTH = 280;
const SIDEBAR_CLOSED_WIDTH = 64;

type Props = {
  readonly children: ReactNode;
};

const checkIfRouteAllowsDocking = ({ pathname }: { pathname: string }) => {
  if (!regexDiscoverJobs.test(pathname) && regexDiscover.test(pathname)) {
    return false;
  }

  if (regexCommunity.test(pathname)) {
    return false;
  }

  return true;
};

export const SidebarContextProvider = ({ children }: Props) => {
  const currentUser = useCurrentUser();
  const hasExpandedSidebarUiFlag = useMemo(() => {
    return (
      currentUser?.userInterfaceFlags.includes('DOCKED_SIDE_NAVIGATION') ??
      false
    );
  }, [currentUser?.userInterfaceFlags]);

  const isSmallerThanDesktop = useMediaQuery('(max-width:1390px)');
  const isExtraLarge = useMediaQuery('(min-width:1140px)');
  const isUltrawide = useMediaQuery('(min-width:1500px)');
  const isMobile = useIsMobile();

  const theme = useTheme();

  const pageContext = usePageContext();

  const canExpandOnRoute = checkIfRouteAllowsDocking({
    pathname: pageContext.urlParsed.pathname,
  });

  const [isExpanded, setIsExpanded] = useState(() => {
    if (isUltrawide && hasExpandedSidebarUiFlag) {
      return true;
    }

    if (
      isMobile ||
      isSmallerThanDesktop ||
      !hasExpandedSidebarUiFlag ||
      !canExpandOnRoute
    ) {
      return false;
    }

    return true;
  });
  const [isOpenOnMobile, setIsOpenOnMobile] = useState(false);

  const router = useRouter();

  const [addUserInterfaceFlag] =
    useContraMutation<SidebarContextProviderAddUIFlagMutation>(graphql`
      mutation SidebarContextProviderAddUIFlagMutation(
        $input: AddUserInterfaceFlagInput!
      ) {
        addUserInterfaceFlag(input: $input) {
          errors {
            __typename
            message
          }
          userAccount {
            id
            userInterfaceFlags
          }
        }
      }
    `);

  const [removeUserInterfaceFlag] =
    useContraMutation<SidebarContextProviderRemoveUIFlagMutation>(graphql`
      mutation SidebarContextProviderRemoveUIFlagMutation(
        $input: RemoveUserInterfaceFlagInput!
      ) {
        removeUserInterfaceFlag(input: $input) {
          errors {
            __typename
            message
          }
          userAccount {
            id
            userInterfaceFlags
          }
        }
      }
    `);

  const setUiFlag = useCallback(() => {
    if (!currentUser?.id) {
      return;
    }

    setIsExpanded(true);

    addUserInterfaceFlag({
      onCompleted: () => {},
      onError: (error) => {
        captureException(error);
      },
      variables: {
        input: {
          userInterfaceFlag: 'DOCKED_SIDE_NAVIGATION',
        },
      },
    });
  }, [addUserInterfaceFlag, currentUser?.id]);

  const unsetUiFlag = useCallback(() => {
    if (!currentUser?.id) {
      return;
    }

    setIsExpanded(false);

    removeUserInterfaceFlag({
      onCompleted: () => {},
      onError: (error) => {
        captureException(error);
      },
      variables: {
        input: {
          userInterfaceFlag: 'DOCKED_SIDE_NAVIGATION',
        },
      },
    });
  }, [currentUser?.id, removeUserInterfaceFlag]);

  const desktop = useMemo(() => {
    return isMobile
      ? null
      : ({
          canExpandOnRoute: isUltrawide
            ? true
            : canExpandOnRoute
              ? Boolean(isExtraLarge)
              : false,
          collapse: unsetUiFlag,
          expand: setUiFlag,
          isExpanded,
        } satisfies SidebarContextType['desktop']);
  }, [
    canExpandOnRoute,
    isExpanded,
    isExtraLarge,
    isMobile,
    isUltrawide,
    setUiFlag,
    unsetUiFlag,
  ]);

  const mobile = useMemo(() => {
    return isMobile
      ? ({
          close: () => setIsOpenOnMobile(false),
          isOpen: isOpenOnMobile,
          open: () => setIsOpenOnMobile(true),
        } satisfies SidebarContextType['mobile'])
      : null;
  }, [isMobile, isOpenOnMobile]);

  const contextValue = useMemo(
    () =>
      ({
        desktop,
        disableScrollPrevention: !(mobile?.isOpen ?? false),
        isMenuExpanded: mobile?.isOpen ?? desktop?.isExpanded ?? false,
        layout: Boolean(mobile) || desktop?.isExpanded ? 'DEFAULT' : 'COMPACT',
        mobile,
        style: {
          display: isOpenOnMobile || desktop ? 'block' : 'none',
          paddingRight: isMobile ? '0px' : desktop?.isExpanded ? '24px' : '0px',
          width: isOpenOnMobile
            ? '100%'
            : desktop?.isExpanded
              ? `${SIDEBAR_OPEN_WIDTH + 24}px`
              : `${SIDEBAR_CLOSED_WIDTH}px`,
          zIndex: isMobile
            ? theme.zIndices.menu + 1
            : theme.zIndices.toolbar + 1,
        },
      }) satisfies SidebarContextType,
    [
      desktop,
      isMobile,
      isOpenOnMobile,
      mobile,
      theme.zIndices.menu,
      theme.zIndices.toolbar,
    ],
  );

  useMount(() => {
    return router.subscribe({
      onTransitionEnd: () => {
        setIsOpenOnMobile(false);
      },
    });
  });

  // Auto expand/collapse sidebar logic
  // Discover/Community pages on ultrawide, rest on extra large
  useEffect(() => {
    if (canExpandOnRoute) {
      if (isExtraLarge && !isExpanded) {
        setIsExpanded(true);
      }

      if (!isExtraLarge && isExpanded) {
        setIsExpanded(false);
      }
    } else {
      if (isUltrawide && !isExpanded) {
        setIsExpanded(true);
      }

      if (!isUltrawide && isExpanded) {
        setIsExpanded(false);
      }
    }
    // This should only run when the screen size changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isExtraLarge, isUltrawide]);

  // Force collapse when exceeding category width limit
  useEffect(() => {
    if (!isUltrawide && !canExpandOnRoute && isExpanded) {
      setIsExpanded(false);
    }

    if (!isExtraLarge && canExpandOnRoute && isExpanded) {
      setIsExpanded(false);
    }
  }, [
    canExpandOnRoute,
    hasExpandedSidebarUiFlag,
    isExpanded,
    isExtraLarge,
    isUltrawide,
  ]);

  // Restore expanded state from ui flag on route changes
  useEffect(() => {
    if (!isMobile && canExpandOnRoute && hasExpandedSidebarUiFlag) {
      setIsExpanded(true);
    }
    // This should only run when the route changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canExpandOnRoute, hasExpandedSidebarUiFlag, isMobile, location.pathname]);

  return (
    <SidebarContext.Provider value={contextValue}>
      {children}
    </SidebarContext.Provider>
  );
};
